import React from "react";
import { Link } from "react-router-dom";
import Hero from "../../components/Hero/Hero";
import PageFooter from "../../components/PageFooter/PageFooter";
import "./home.css";
import { Helmet } from "react-helmet";

const TOOLS = [
  {
    to: "/audit-google-my-business",
    icon: "📊",
    title: "Audit fiche Google Business Profile",
    description:
      "Réalisez un audit de votre fiche GBP en 1 clic et commencez à optimiser votre fiche pour dépasser vos concurrents locaux.",
    tag: null,
    external: false,
  },
  {
    to: "/audit-store-locator",
    icon: "🏪",
    title: "Audit store locator",
    description:
      "Réalisez un audit de votre store locator en toute autonomie. Collez l'URL d'une page locale et obtenez une analyse complète.",
    tag: "Nouveau",
    external: false,
  },
  {
    to: "/serp-local-google",
    icon: "📍",
    title: "Résultats Google localisés",
    description:
      "Découvrez les résultats de recherche locales sur la requête de votre choix et comparez votre référencement local face à la concurrence.",
    tag: null,
    external: false,
  },
  {
    to: "/reviews-semantic-analysis",
    icon: "💬",
    title: "Analyse sémantique des avis clients",
    description:
      "Analysez le sentiment et les thématiques clés de vos avis clients pour identifier vos forces et axes d'amélioration.",
    tag: "Nouveau",
    external: false,
  },
  {
    to: "/missing-reviews-calculator",
    icon: "⭐",
    title: "Calculateur d'avis manquants",
    description:
      "Indiquez votre nombre d'avis et votre note actuelle, et découvrez combien d'avis 5 étoiles il vous faut pour atteindre votre objectif.",
    tag: "Nouveau",
    external: false,
  },
  {
    to: "/local-business-structured-data-generator",
    icon: "🔧",
    title: "Générateur de données structurées locales",
    description:
      "Générez facilement des données structurées JSON-LD pour votre entreprise locale et améliorez votre référencement dans les résultats Google.",
    tag: null,
    external: false,
  },
  {
    to: "/google-business-profile-categories",
    icon: "🏷️",
    title: "Recherche de catégories Google Business Profile",
    description:
      "Trouvez et copiez les catégories GBP parfaites pour votre fiche Google. Recherchez parmi des milliers de catégories en 5 langues.",
    tag: "Nouveau",
    external: false,
  },
];

const ToolCard = ({ tool }) => {
  const inner = (
    <div className="tool-card">
      <div className="tool-card__icon">{tool.icon}</div>
      <div className="tool-card__body">
        <div className="tool-card__header">
          <h3 className="tool-card__title">{tool.title}</h3>
          {tool.tag && <span className="tool-card__tag">{tool.tag}</span>}
        </div>
        <p className="tool-card__desc">{tool.description}</p>
        <span className="tool-card__link">Tester l'outil →</span>
      </div>
    </div>
  );

  if (tool.external) {
    return (
      <a href={tool.to} target="_blank" rel="noopener noreferrer">
        {inner}
      </a>
    );
  }
  return <Link to={tool.to}>{inner}</Link>;
};

const Home = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const hasLocalRankerAccess = urlParams.get("access") === "localranker";

  const allTools = hasLocalRankerAccess
    ? [
        ...TOOLS,
        {
          to: "/search-extraction-google",
          icon: "🔍",
          title: "Extraction Recherche avancée Google",
          description:
            "Construisez facilement des requêtes de recherche Google avancées avec des opérateurs comme inurl:, site:, et des termes combinés.",
          tag: null,
          external: false,
        },
      ]
    : TOOLS;

  return (
    <div className="home">
      <Helmet>
        <title>Outils de visibilité locale gratuits | Localranker</title>
        <meta
          name="description"
          content="Vous voulez améliorer votre visibilité locale ? Aidez-vous de nos outils gratuits de référencement local !"
        />
        <link rel="canonical" href="https://localranker.tech/" />
        <meta property="og:title" content="Outils de visibilité locale gratuits | Localranker" />
        <meta property="og:description" content="Vous voulez améliorer votre visibilité locale ? Aidez-vous de nos outils gratuits de référencement local !" />
        <meta property="og:url" content="https://localranker.tech/" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Localranker" />
      </Helmet>

      <Hero
        badge="Répertoire d'outils de visibilité locale"
        title="Nos outils gratuits de visibilité locale"
        subtitle="Des outils gratuits et puissants pour analyser, auditer et optimiser votre présence locale sur Google. Réseaux multi-établissements et agences, demandez une démo."
        ctaText="Demander une démo"
        ctaHref="https://localranker.fr/demander-demo?utm_source=localranker.tech&utm_medium=home"
        compact
      />

      <section className="tools-section">
        <div className="tools-section__inner">
          <div className="tools-grid">
            {allTools.map((tool, i) => (
              <ToolCard key={i} tool={tool} />
            ))}
          </div>
        </div>
      </section>

      <section className="mission-section">
        <div className="mission-section__inner">
          <span className="mission-section__badge">Notre mission</span>
          <h2 className="mission-section__title">
            Pourquoi ces outils sont gratuits ?
          </h2>

          <div className="mission-section__grid">
            <div className="mission-section__block">
              <h3>La visibilité locale ne devrait pas être un privilège</h3>
              <p>
                Trop d'entreprises passent à côté de clients simplement parce
                qu'elles n'ont pas accès aux bons outils d'analyse. Que vous
                soyez un commerce de quartier, un artisan ou un réseau de
                franchises, vous méritez de comprendre comment vous apparaissez
                dans les résultats de recherche locaux, sans avoir à investir
                dans des solutions coûteuses pour obtenir un premier diagnostic.
              </p>
            </div>

            <div className="mission-section__block">
              <h3>Un premier diagnostic, sans engagement</h3>
              <p>
                Nous construisons ces outils parce que nous pensons que la
                meilleure façon de prouver notre expertise, c'est de la
                partager. Ces analyses gratuites vous donnent un aperçu concret
                de votre situation et des pistes d'amélioration claires. C'est
                un point de départ solide pour prendre les bonnes décisions sur
                votre stratégie locale.
              </p>
            </div>

            <div className="mission-section__block">
              <h3>Aider les entreprises à trouver leurs prochains clients</h3>
              <p>
                Notre conviction est simple : chaque entreprise locale devrait
                pouvoir être trouvée en ligne par les clients qui la cherchent.
                Ces outils gratuits sont un premier pas concret. Ils vous
                permettent d'identifier ce qui fonctionne, ce qui manque, et par
                où commencer pour améliorer votre présence locale.
              </p>
            </div>

            <div className="mission-section__block">
              <h3>Aller plus loin avec Localranker</h3>
              <p>
                Ces outils vous donnent une photographie précise de votre
                situation. Pour passer à l'action, gérer vos fiches Google à
                grande échelle, centraliser vos avis, publier du contenu local,
                suivre votre positionnement dans le temps, notre plateforme{" "}
                <strong>Localranker</strong> prend le relais. Réseaux
                multi-établissements et agences l'utilisent au quotidien pour
                piloter leur visibilité locale.
              </p>
            </div>
          </div>

          <div className="mission-section__cta-wrap">
            <a
              className="mission-section__cta"
              href="https://localranker.fr/demander-demo?utm_source=localranker.tech&utm_medium=home-mission"
              target="_blank"
              rel="noopener noreferrer"
            >
              Demander une démo de Localranker
            </a>
          </div>
        </div>
      </section>

      <PageFooter />
    </div>
  );
};

export default Home;
